import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LineChart as LineIcon, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const METRICS = [
  { key: "revpar", it: "RevPAR", en: "RevPAR", unit: "€" },
  { key: "adr", it: "ADR", en: "ADR", unit: "€" },
  { key: "occupancy", it: "Occupazione", en: "Occupancy", unit: "%" },
  { key: "revenue", it: "Ricavo", en: "Revenue", unit: "€" },
];

export default function PerformanceTrend({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [gran, setGran] = useState("month");
  const [metric, setMetric] = useState("revpar");
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ granularity: gran, lang });
      if (year) p.set("year", year);
      if (gran === "day" && month) p.set("month", month);
      const { data } = await api.get(`/properties/${propId}/performance-trend?${p.toString()}`);
      setD(data);
      if (data.year && !year) setYear(data.year);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId, gran, year, month, lang]);
  useEffect(() => { load(); }, [load]);

  const m = METRICS.find((x) => x.key === metric);
  const unit = m?.unit || "";
  const fmt = (v) => (v == null ? "—" : unit === "€" ? `€${Math.round(v).toLocaleString()}` : `${v}%`);
  const chart = (d?.series || []).map((p) => ({ label: p.label, cur: p[metric] }));
  const yoy = d?.yoy;
  if (yoy && gran === "month") {
    yoy.series.forEach((y, i) => { if (chart[i]) chart[i].prev = y.previous[metric]; });
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="performance-trend">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><LineIcon className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Andamento", "Trend")}</h2></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="trend-granularity">
            {[["year", L("Anno", "Year")], ["month", L("Mese", "Month")], ["day", L("Giorno", "Day")]].map(([k, lbl]) => (
              <button key={k} onClick={() => setGran(k)} data-testid={`trend-gran-${k}`}
                className={`px-3 h-8 text-xs font-medium ${gran === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>{lbl}</button>
            ))}
          </div>
          {gran !== "year" && (
            <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} data-testid="trend-year"
              className="h-8 rounded-md border border-border bg-background px-2 text-sm">
              {(d?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          {gran === "day" && (
            <select value={month || ""} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : null)} data-testid="trend-month"
              className="h-8 rounded-md border border-border bg-background px-2 text-sm">
              <option value="">{L("Tutto l'anno", "All year")}</option>
              {(d?.months || []).map((mo) => <option key={mo.n} value={mo.n}>{mo.label}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading && !d ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>
        : !d?.has_data ? <p className="text-sm text-muted-foreground py-6" data-testid="trend-empty">{d?.message}</p>
        : (
        <>
          {/* metric selector */}
          <div className="flex gap-2 flex-wrap" data-testid="trend-metrics">
            {METRICS.map((x) => (
              <button key={x.key} onClick={() => setMetric(x.key)} data-testid={`trend-metric-${x.key}`}
                className={`px-3 h-8 rounded-full text-xs font-medium border ${metric === x.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                {L(x.it, x.en)}
              </button>
            ))}
          </div>

          {/* totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trend-totals">
            {[["ADR", d.totals.adr, "€"], ["RevPAR", d.totals.revpar, "€"], [L("Occupazione", "Occupancy"), d.totals.occupancy, "%"], [L("Ricavo", "Revenue"), d.totals.revenue, "€"]].map(([k, v, u], i) => (
              <div key={i} className="rounded-lg border border-border p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{k}</p>
                <p className="text-xl font-bold mt-0.5">{v == null ? "—" : u === "€" ? `€${Math.round(v).toLocaleString()}` : `${v}%`}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border p-4">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chart} margin={{ top: 6, right: 10, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={gran === "day" ? "preserveStartEnd" : 0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                {yoy && gran === "month" && <Legend wrapperStyle={{ fontSize: 12 }} />}
                <Line type="monotone" dataKey="cur" name={`${L(m.it, m.en)} ${d.year || ""}`} stroke="#2563eb" strokeWidth={2.5} dot={{ r: 2 }} />
                {yoy && gran === "month" && <Line type="monotone" dataKey="prev" name={`${yoy.prev_year}`} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2 }} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </motion.div>
  );
}
